import type {
  ClusterOverviewItem,
  OrganizationOverviewAgentResourceMetrics,
  OrganizationOverviewCloudResourceMetrics,
  OrganizationOverviewResourceTimeseriesPoint,
  OrganizationOverviewSummary,
} from '@cast/types';
import { toNumOrZero } from '@cast/utils';

/** Cloud metrics may use 0–1; headline fields use percentage (e.g. 21.00). */
export const normalizeUtilizationPercent = (value: number): number => {
  if (value <= 0) {
    return 0;
  }

  return value <= 1 ? value * 100 : value;
};

export const deriveUsedFromProvisionedAndUtilization = (
  provisioned: number,
  utilization?: number
): number => {
  const utilPct = normalizeUtilizationPercent(toNumOrZero(utilization));

  return provisioned > 0 && utilPct > 0 ? (provisioned * utilPct) / 100 : 0;
};

export const resolveCpuUsed = (
  item: ClusterOverviewItem,
  agentMetrics?: OrganizationOverviewAgentResourceMetrics,
  cloudMetrics?: OrganizationOverviewCloudResourceMetrics
): number => {
  const agentUsed = toNumOrZero(agentMetrics?.cpuUsed);
  if (agentUsed > 0) {
    return agentUsed;
  }

  const provisioned =
    toNumOrZero(item.cpuProvisioned) ||
    toNumOrZero(agentMetrics?.cpuProvisioned) ||
    toNumOrZero(cloudMetrics?.cpuProvisioned);
  const avgUtil =
    toNumOrZero(item.avgCpuUtilization) ||
    toNumOrZero(cloudMetrics?.avgCpuUtilization);

  return deriveUsedFromProvisionedAndUtilization(provisioned, avgUtil);
};

export const resolveRamUsed = (
  item: ClusterOverviewItem,
  agentMetrics?: OrganizationOverviewAgentResourceMetrics,
  cloudMetrics?: OrganizationOverviewCloudResourceMetrics
): number => {
  const agentUsed = toNumOrZero(agentMetrics?.ramUsed);
  if (agentUsed > 0) {
    return agentUsed;
  }

  const provisioned =
    toNumOrZero(item.ramProvisioned) ||
    toNumOrZero(agentMetrics?.ramProvisioned) ||
    toNumOrZero(cloudMetrics?.ramProvisioned);
  const avgUtil =
    toNumOrZero(item.avgRamUtilization) ||
    toNumOrZero(cloudMetrics?.avgRamUtilization);

  return deriveUsedFromProvisionedAndUtilization(provisioned, avgUtil);
};

export const resolveFleetResourceTotals = (
  summary?: OrganizationOverviewSummary,
  lastPoint?: OrganizationOverviewResourceTimeseriesPoint
) => {
  const totalCpuProvisioned =
    toNumOrZero(summary?.totalCpuProvisioned) ||
    toNumOrZero(lastPoint?.cpuProvisioned);
  const totalRamProvisioned =
    toNumOrZero(summary?.totalRamProvisioned) ||
    toNumOrZero(lastPoint?.ramProvisionedGib);
  const totalGpuProvisioned = toNumOrZero(summary?.totalGpuProvisioned);

  const totalCpuUsed =
    toNumOrZero(lastPoint?.cpuUsed) ||
    deriveUsedFromProvisionedAndUtilization(
      totalCpuProvisioned,
      toNumOrZero(lastPoint?.cpuUtilization) ||
        toNumOrZero(summary?.avgCpuUtilization)
    );

  const totalRamUsed =
    toNumOrZero(lastPoint?.ramUsedGib) ||
    deriveUsedFromProvisionedAndUtilization(
      totalRamProvisioned,
      toNumOrZero(lastPoint?.ramUtilization) ||
        toNumOrZero(summary?.avgRamUtilization)
    );

  // Org overview API exposes GPU provisioned only (no gpuUsed on summary/timeseries).
  const totalGpuUsed = 0;

  return {
    totalCpuProvisioned,
    totalRamProvisioned,
    totalGpuProvisioned,
    totalCpuUsed,
    totalRamUsed,
    totalGpuUsed,
  };
};
